import { createFilterOptions } from '@mui/material/Autocomplete';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { HTMLAttributes, Key, KeyboardEvent, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { VirtualizedAutocompleteNavigation, VirtualizedAutocompleteOption } from './virtualized_autocomplete_listbox';

// Single-select inputs keep MUI's native inputValue/pristine behavior. This
// mirrors input changes only to reset the virtual window, not to control input.
export function useVirtualizedAutocomplete<T>(sourceOptions: T[], getOptionLabel: (option: T) => string, multiline = false) {
  const [inputValue, setInputValue] = useState('');
  const navigationRef = useRef<VirtualizedAutocompleteNavigation | null>(null);
  const nativeFilter = useMemo(() => createFilterOptions<T>(), []);
  const cache = useRef<{
    options: T[];
    state: FilterOptionsState<T>;
    results: T[];
    indices: Map<T, number>;
  }>();

  const filterOptions = useCallback((options: T[], state: FilterOptionsState<T>) => {
    const previous = cache.current;
    // MUI passes a freshly copied array on every render; these wrappers do not
    // hide selected options, so the original options reference is our cache key.
    if (previous && previous.options === sourceOptions && previous.state.inputValue === state.inputValue
      && previous.state.getOptionLabel === getOptionLabel) return previous.results;
    const results = nativeFilter(options, state);
    // useAutocomplete also wraps getOptionLabel on every render. Compare the
    // supplied getter, while still using MUI's normalized getter for filtering.
    cache.current = { options: sourceOptions, state: { ...state, getOptionLabel }, results,
      indices: new Map(results.map((option, index) => [option, index])) };
    return results;
  }, [nativeFilter, sourceOptions, getOptionLabel]);

  const onInputChange = useCallback((_: unknown, value: string) => setInputValue(value), []);
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => navigationRef.current?.prepareKeyDown(event), []);
  const onHighlightChange = useCallback((_: unknown, option: T | null, reason: 'keyboard' | 'mouse' | 'touch') => {
    navigationRef.current?.highlight(option === null ? -1 : cache.current?.indices.get(option) ?? -1, reason);
  }, []);
  const renderOption = useCallback((props: HTMLAttributes<HTMLLIElement> & { key: Key }, option: T, state: { selected: boolean }) => ({
    props, label: getOptionLabel(option), selected: state.selected, showCheckbox: false,
  } satisfies VirtualizedAutocompleteOption) as unknown as ReactNode, [getOptionLabel]);
  const contextValue = useMemo(() => ({ navigationRef, inputValue, multiline }), [inputValue, multiline]);

  return { contextValue, filterOptions, onInputChange, onKeyDown, onHighlightChange, renderOption };
}
